/**
 * baseHole 类型策略：底座孔（矩形 + 绕中心旋转 rotationRad）。
 *
 * 几何：
 *   - width / height = mm × mmToPixelRatio / maskFitScale
 *   - centerPoint = canvas 坐标（矩形几何中心）
 *   - rotationRad = 绕中心旋转弧度
 *
 * 手势（spec §4.9）：
 *   - 本体 drag → 平移（不改 rotationRad）
 *   - icon drag → 以矩形中心为轴，按手指相对中心的角度变化更新 rotationRad
 *   - icon tap（位移 < iconTapMoveThresholdPx）→ 不响应（撤回拖动期间的旋转）
 *
 * 轮廓约束（spec §4.9 R1）：
 *   - 当前底座孔固定 0 度不可旋转；clampSelf 用 8 点采样 + 线段最近点迭代
 *   - 仍无法满足 R1 → clampSelf 返回 true（outline 冲突）
 * R2（刀线点不得入孔内）由 collision.evaluateOutlinePointsInBaseHoles 检测
 * 组件间碰撞：getCollisionShape 仍用旋转后 AABB（首版）
 *
 * 默认位置（spec §5）：
 *   - 首个实例 → 轮廓几何中心
 *   - 后续实例 → 在已有同 type 最右实例右边偏移 minGapPx + 平均尺寸
 *
 * 导出（spec §7）：
 *   - baseHoleInfoList:       [{ centerPoint:{x,y}, width, height, rotationRad }]（centerPoint/width/height 均为比例 0~1）
 *   - originBaseHoleInfoList: [{ centerPoint, width, height, rotationRad, baseCanvasWidth, canvasRoundWhiteSpace, componentId, geometryConfig, ... }]（canvas px）
 */

import {
  convertCanvasToBackend,
  getRestoreBusinessScale,
  isPointInPolygon
} from '../core.js';
import {
  getRotatedRectProbePoints,
  isRotatedRectInsideOutline
} from '../geometry/rotatedRect.js';
import {
  DRAW_TYPE,
  lineColor,
  roundColor,
  DEFAULT_CONFIG
} from '../constants.js';
import { registerComponentType } from '../typeRegistry.js';

const CLAMP_MAX_ITER = 8;

let _iconImgCache = null;
/**
 * 懒加载底座孔旋转 icon（DEFAULT_CONFIG.baseHoleRotateIcon.src）。
 * @returns {HTMLImageElement|null}
 */
function loadBaseHoleRotateIcon() {
  if (_iconImgCache !== null) return _iconImgCache;
  const cfg = DEFAULT_CONFIG.baseHoleRotateIcon;
  if (!cfg || !cfg.src) {
    _iconImgCache = null;
    return null;
  }
  const img = new Image();
  img.src = cfg.src;
  _iconImgCache = img;
  return img;
}

/**
 * 读取底座孔旋转 icon 配置项。
 * @returns {{size:number,marginTop:number}}
 */
function getBaseHoleIconConfig() {
  const cfg = DEFAULT_CONFIG.baseHoleRotateIcon || {};
  return {
    size: cfg.size != null ? cfg.size : 28,
    marginTop: cfg.marginTop != null ? cfg.marginTop : 6
  };
}

function validateGeometryConfig(g) {
  if (!g) return '缺少组件尺寸配置：geometryConfig';
  if (typeof g.widthMm !== 'number' || g.widthMm <= 0) {
    return '缺少或非法字段：widthMm（必须 > 0 的数字）';
  }
  if (typeof g.heightMm !== 'number' || g.heightMm <= 0) {
    return '缺少或非法字段：heightMm（必须 > 0 的数字）';
  }
  return null;
}

function computeSize(geometryConfig, sharedState) {
  const k = sharedState.mmToPixelRatio / (sharedState.maskFitScale || 1);
  return {
    width: geometryConfig.widthMm * k,
    height: geometryConfig.heightMm * k
  };
}

/**
 * 按后台下发的 mm 圆角计算 canvas 圆角半径。
 * @param {number} width - 底座孔宽度 px
 * @param {number} height - 底座孔高度 px
 * @param {number} radiusMm - 圆角半径 mm
 * @param {Object} sharedState - 共享 canvas 状态
 * @returns {number}
 */
function computeCornerRadiusByMm(width, height, radiusMm, sharedState) {
  const k = sharedState.mmToPixelRatio / (sharedState.maskFitScale || 1);
  const raw = Math.max(0, radiusMm) * k;
  const max = Math.min(width, height) / 2;
  return Math.max(0, Math.min(raw, max));
}

/**
 * 根据组件配置计算底座孔圆角半径。
 * @param {Object} geometryConfig - 底座孔 geometryConfig
 * @param {Object} sharedState - 共享 canvas 状态
 * @param {number} width - 底座孔宽度 px
 * @param {number} height - 底座孔高度 px
 * @returns {number}
 */
function computeCornerRadius(geometryConfig, sharedState, width, height) {
  const g = geometryConfig || {};
  // radius 明确存在时使用后台下发的 mm 圆角，允许 0 表示直角。
  if (typeof g.radius === 'number' && isFinite(g.radius) && g.radius >= 0) {
    return computeCornerRadiusByMm(width, height, g.radius, sharedState);
  }
  return 0;
}

/**
 * 获取底座孔实例上的圆角半径。
 * @param {Object} instance - 底座孔实例
 * @returns {number}
 */
function getCornerRadius(instance) {
  // 新实例在 init 阶段会写入 cornerRadius，历史运行态缺失时按直角处理。
  if (
    typeof instance.cornerRadius === 'number' &&
    isFinite(instance.cornerRadius)
  ) {
    return instance.cornerRadius;
  }
  return 0;
}

/**
 * 构建圆角矩形路径；radius 为 0 时退化为普通矩形。
 * @param {CanvasRenderingContext2D} ctx - canvas 上下文
 * @param {number} x - 左上角 x
 * @param {number} y - 左上角 y
 * @param {number} w - 宽度
 * @param {number} h - 高度
 * @param {number} radius - 圆角半径
 * @returns {void}
 */
function buildRoundRectPath(ctx, x, y, w, h, radius) {
  const r = Math.max(0, Math.min(radius || 0, w / 2, h / 2));
  ctx.beginPath();
  // 无圆角时保持普通矩形路径
  if (r <= 0) {
    ctx.rect(x, y, w, h);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * 求轮廓外接矩形中心（与 perforation 一致）。
 */
function getOutlineCenter(points) {
  if (!points || !points.length) return { x: 0, y: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}

/**
 * 找到点到刀线折线的最近点。
 * @param {{x:number,y:number}} point - 输入点
 * @param {Array<Array<number>>} points - 刀线点 [[x,y],...]
 * @returns {{x:number,y:number,distance:number}|null}
 */
function findClosestPointOnOutline(point, points) {
  // 无刀线点时无法计算最近点
  if (!points || !points.length) return null;
  let minSq = Infinity;
  let closest = null;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const lenSq = abx * abx + aby * aby;
    let t = 0;
    // 线段长度有效时，计算点在线段上的投影比例
    if (lenSq > 0) {
      t = Math.max(
        0,
        Math.min(1, ((point.x - a[0]) * abx + (point.y - a[1]) * aby) / lenSq)
      );
    }
    const x = a[0] + abx * t;
    const y = a[1] + aby * t;
    const dx = x - point.x;
    const dy = y - point.y;
    const sq = dx * dx + dy * dy;
    // 记录当前最近的线段投影点
    if (sq < minSq) {
      minSq = sq;
      closest = { x: x, y: y, distance: Math.sqrt(sq) };
    }
  }
  return closest;
}

/**
 * 获取轮廓外接矩形中心到 point 的方向。
 * @param {{x:number,y:number}} point - 输入点
 * @param {Array<Array<number>>} points - 刀线点 [[x,y],...]
 * @returns {{x:number,y:number}}
 */
function getDirectionToOutlineCenter(point, points) {
  const center = getOutlineCenter(points);
  const dx = center.x - point.x;
  const dy = center.y - point.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  // 轮廓中心与输入点重合时，使用向下方向兜底
  if (dist < 1e-6) {
    return { x: 0, y: 1 };
  }
  return { x: dx / dist, y: dy / dist };
}

/**
 * 判断轴对齐底座孔是否满足刀线内约束和最小间距。
 * @param {Object} instance - 底座孔实例
 * @param {Array<Array<number>>} points - 刀线点 [[x,y],...]
 * @param {number} outlineGap - 最小间距 px
 * @param {number} samplesPerEdge - 每条边采样数量
 * @returns {boolean}
 */
function isAxisAlignedBaseHoleOutlineSafe(
  instance,
  points,
  outlineGap,
  samplesPerEdge
) {
  // 探测点不全在轮廓内时，底座孔不满足刀线内约束
  if (!isRotatedRectInsideOutline(instance, points, samplesPerEdge)) {
    return false;
  }
  // 未配置最小间距时，只需满足刀线内约束
  if (outlineGap <= 0) {
    return true;
  }
  const probes = getRotatedRectProbePoints(instance, samplesPerEdge);
  for (let i = 0; i < probes.length; i++) {
    const closest = findClosestPointOnOutline(probes[i], points);
    // 找不到最近点时视为不满足约束
    if (!closest) return false;
    // 任一边界采样点到刀线距离不足时，视为不满足最小间距
    if (closest.distance < outlineGap - 0.01) return false;
  }
  return true;
}

/**
 * 获取采样点相对刀线的违规信息。
 * @param {{x:number,y:number}} probe - 矩形边界采样点
 * @param {Array<Array<number>>} points - 刀线点 [[x,y],...]
 * @param {number} outlineGap - 最小间距 px
 * @returns {{amount:number,ux:number,uy:number}|null}
 */
function getProbeOutlineViolation(probe, points, outlineGap) {
  const closest = findClosestPointOnOutline(probe, points);
  // 找不到最近刀线点时无法计算违规信息
  if (!closest) return null;

  const inside = isPointInPolygon(probe, points);
  let ux;
  let uy;

  if (closest.distance < 1e-6) {
    const fallback = getDirectionToOutlineCenter(probe, points);
    ux = fallback.x;
    uy = fallback.y;
  } else if (inside) {
    ux = (probe.x - closest.x) / closest.distance;
    uy = (probe.y - closest.y) / closest.distance;
  } else {
    ux = (closest.x - probe.x) / closest.distance;
    uy = (closest.y - probe.y) / closest.distance;
  }

  // 采样点在刀线外时，先推回边界，再额外推入最小间距
  if (!inside) {
    return {
      amount: closest.distance + outlineGap,
      ux: ux,
      uy: uy
    };
  }
  // 采样点在刀线内但间距不足时，只补足差值
  if (closest.distance < outlineGap - 0.01) {
    return {
      amount: outlineGap - closest.distance,
      ux: ux,
      uy: uy
    };
  }
  return null;
}

/**
 * 找到当前最严重的底座孔边界违规点。
 * @param {Object} instance - 底座孔实例
 * @param {Array<Array<number>>} points - 刀线点 [[x,y],...]
 * @param {number} outlineGap - 最小间距 px
 * @param {number} samplesPerEdge - 每条边采样数量
 * @returns {{amount:number,ux:number,uy:number}|null}
 */
function getWorstBaseHoleViolation(
  instance,
  points,
  outlineGap,
  samplesPerEdge
) {
  const probes = getRotatedRectProbePoints(instance, samplesPerEdge);
  let worst = null;
  for (let i = 0; i < probes.length; i++) {
    const violation = getProbeOutlineViolation(probes[i], points, outlineGap);
    // 当前采样点无违规时继续检查后续点
    if (!violation) continue;
    // 记录需要推回距离最大的违规点
    if (!worst || violation.amount > worst.amount) {
      worst = violation;
    }
  }
  return worst;
}

/**
 * 以参考实例为基准，向右偏移 minGapPx + (w1+w2)/2 作为新位置。
 * @param {Object} referenceInstance - 参考实例（最近选中或最右侧）
 * @param {number} width - 新实例宽度
 * @param {number} minGapPx
 * @returns {{x:number,y:number}}
 */
function getNextRightOf(referenceInstance, width, minGapPx) {
  return {
    x:
      referenceInstance.centerPoint.x +
      referenceInstance.width / 2 +
      minGapPx +
      width / 2,
    y: referenceInstance.centerPoint.y
  };
}

/**
 * 取矩形旋转后的 4 个角点（canvas 坐标）。
 * @param {Object} instance
 * @returns {Array<{x:number,y:number}>}
 */
function getRotatedCorners(instance) {
  const cx = instance.centerPoint.x;
  const cy = instance.centerPoint.y;
  const w = instance.width;
  const h = instance.height;
  const cos = Math.cos(instance.rotationRad);
  const sin = Math.sin(instance.rotationRad);
  const local = [
    { x: -w / 2, y: -h / 2 },
    { x: w / 2, y: -h / 2 },
    { x: w / 2, y: h / 2 },
    { x: -w / 2, y: h / 2 }
  ];
  return local.map(function (p) {
    return {
      x: cx + p.x * cos - p.y * sin,
      y: cy + p.x * sin + p.y * cos
    };
  });
}

/**
 * 旋转后角点的 AABB（用于碰撞 / 选中外接矩形）。
 */
function getRotatedAABB(instance) {
  const corners = getRotatedCorners(instance);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const c of corners) {
    if (c.x < minX) minX = c.x;
    if (c.x > maxX) maxX = c.x;
    if (c.y < minY) minY = c.y;
    if (c.y > maxY) maxY = c.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * 将 canvas 点转换为底座孔局部坐标。
 * @param {Object} instance - 底座孔实例
 * @param {{x:number,y:number}} point - canvas 坐标点
 * @returns {{x:number,y:number}} 局部坐标
 */
function pointToBaseHoleLocal(instance, point) {
  const dx = point.x - instance.centerPoint.x;
  const dy = point.y - instance.centerPoint.y;
  const cos = Math.cos(-instance.rotationRad);
  const sin = Math.sin(-instance.rotationRad);
  return {
    x: dx * cos - dy * sin,
    y: dx * sin + dy * cos
  };
}

/**
 * init：解析尺寸 + 初始位置（placement 优先，否则按 §5.1/§5.2 默认）。
 * @param {Object} instance
 * @param {Object} sharedState
 * @param {Object} options
 * @param {Object} [options.placement] - { centerPoint, rotationRad }
 * @param {Array<Object>} [options.sameTypeInstances]
 * @param {Object} [options.referenceInstance] - 同类型右偏移参考实例
 * @param {number} [options.minGapPx]
 * @param {number} [options.crossTypeOffsetY] - 穿孔↔底座孔交叉偏移（px）
 * @returns {void}
 */
function init(instance, sharedState, options) {
  const size = computeSize(instance.geometryConfig, sharedState);
  instance.width = size.width;
  instance.height = size.height;
  instance.cornerRadius = computeCornerRadius(
    instance.geometryConfig,
    sharedState,
    instance.width,
    instance.height
  );

  const placement = (options && options.placement) || null;
  let centerPoint = null;
  let rotationRad = 0;
  if (placement) {
    if (typeof placement.rotationRad === 'number') {
      rotationRad = placement.rotationRad;
    }
    if (placement.centerPoint) {
      const cp = placement.centerPoint;
      // 按去除留白后的业务坐标宽度缩放，避免 padding 变化导致还原位置偏移。
      if (
        cp.baseCanvasWidth &&
        cp.baseCanvasWidth !== sharedState.canvasWidth
      ) {
        const k = getRestoreBusinessScale(cp, sharedState);
        centerPoint = { x: cp.x * k, y: cp.y * k };
      } else {
        centerPoint = { x: cp.x, y: cp.y };
      }
    }
  }
  if (!centerPoint) {
    const ref = (options && options.referenceInstance) || null;
    const minGapPx = (options && options.minGapPx) || 0;
    if (ref) {
      centerPoint = getNextRightOf(ref, instance.width, minGapPx);
    }
    if (!centerPoint) {
      centerPoint = getOutlineCenter(sharedState.outlinePoints);
      // 交叉偏移：穿孔↔底座孔首次添加时向下偏移避免重叠
      const crossOffsetY = (options && options.crossTypeOffsetY) || 0;
      if (crossOffsetY) {
        centerPoint = { x: centerPoint.x, y: centerPoint.y + crossOffsetY };
      }
    }
  }
  instance.centerPoint = centerPoint;
  instance.rotationRad = DEFAULT_CONFIG.baseHoleRotationEnabled
    ? rotationRad
    : 0;
}

/**
 * clampSelf：按当前底座孔固定 0 度的业务约束，迭代平移中心，
 * 使轴对齐矩形满足 R1（8 点采样在刀线多边形内）并保持与刀线的最小间距。
 * 仍无法满足 → return true（outline 冲突）。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {boolean}
 */
function clampSelf(instance, sharedState) {
  const points = sharedState.outlinePoints;
  if (!points || !points.length) return false;
  const samplesPerEdge = DEFAULT_CONFIG.baseHoleRectInsideSamplesPerEdge;
  const outlineGap = sharedState.componentToOutlineGapPx || 0;

  let iter = 0;
  while (iter < CLAMP_MAX_ITER) {
    // 轴对齐底座孔已满足刀线内约束和最小间距时停止收敛
    if (
      isAxisAlignedBaseHoleOutlineSafe(
        instance,
        points,
        outlineGap,
        samplesPerEdge
      )
    ) {
      break;
    }
    const violation = getWorstBaseHoleViolation(
      instance,
      points,
      outlineGap,
      samplesPerEdge
    );
    // 没有明确违规点时，继续迭代没有收益
    if (!violation) break;
    const oldX = instance.centerPoint.x;
    const oldY = instance.centerPoint.y;
    instance.centerPoint.x += violation.ux * violation.amount;
    instance.centerPoint.y += violation.uy * violation.amount;
    // 本轮没有可见位移时，继续迭代没有收益
    if (
      Math.abs(instance.centerPoint.x - oldX) <= 0.01 &&
      Math.abs(instance.centerPoint.y - oldY) <= 0.01
    ) {
      break;
    }
    iter++;
  }
  return !isAxisAlignedBaseHoleOutlineSafe(
    instance,
    points,
    outlineGap,
    samplesPerEdge
  );
}

/**
 * 命中检测：把 point 反旋转到矩形局部坐标，再判 [-w/2,w/2]×[-h/2,h/2]。
 */
function hitTest(instance, point) {
  const local = pointToBaseHoleLocal(instance, point);
  return (
    local.x >= -instance.width / 2 &&
    local.x <= instance.width / 2 &&
    local.y >= -instance.height / 2 &&
    local.y <= instance.height / 2
  );
}

/**
 * 获取编辑态命中信息；小尺寸底座孔按中心补足到最小热区。
 * @param {Object} instance - 底座孔实例
 * @param {{x:number,y:number}} point - canvas 坐标点
 * @returns {{hit:boolean,isRealHit:boolean,distance:number}}
 */
function getHitTestInfo(instance, point) {
  const local = pointToBaseHoleLocal(instance, point);
  const isRealHit =
    local.x >= -instance.width / 2 &&
    local.x <= instance.width / 2 &&
    local.y >= -instance.height / 2 &&
    local.y <= instance.height / 2;
  const hitWidth = Math.max(
    instance.width + DEFAULT_CONFIG.componentLargeHitPaddingPx * 2,
    DEFAULT_CONFIG.componentMinHitSizePx
  );
  const hitHeight = Math.max(
    instance.height + DEFAULT_CONFIG.componentLargeHitPaddingPx * 2,
    DEFAULT_CONFIG.componentMinHitSizePx
  );
  const dx = point.x - instance.centerPoint.x;
  const dy = point.y - instance.centerPoint.y;
  return {
    hit:
      local.x >= -hitWidth / 2 &&
      local.x <= hitWidth / 2 &&
      local.y >= -hitHeight / 2 &&
      local.y <= hitHeight / 2,
    isRealHit,
    distance: Math.sqrt(dx * dx + dy * dy)
  };
}

function onDragStart(instance, point) {
  instance._dragOffset = {
    dx: point.x - instance.centerPoint.x,
    dy: point.y - instance.centerPoint.y
  };
}

function onDragMove(instance, point, sharedState) {
  const off = instance._dragOffset || { dx: 0, dy: 0 };
  instance.centerPoint.x = point.x - off.dx;
  instance.centerPoint.y = point.y - off.dy;
  clampSelf(instance, sharedState);
}

function onDragEnd(instance) {
  instance._dragOffset = null;
}

/**
 * 绘制旋转矩形 + 中心点；不在 draw 内 save/translate 外层（outlineRenderer 已经统一布局），
 * 直接用 canvas transform。
 */
function drawRotatedRect(ctx, instance, strokeStyle, lineWidth) {
  ctx.save();
  ctx.translate(instance.centerPoint.x, instance.centerPoint.y);
  ctx.rotate(instance.rotationRad);
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([]);
  buildRoundRectPath(
    ctx,
    -instance.width / 2,
    -instance.height / 2,
    instance.width,
    instance.height,
    getCornerRadius(instance)
  );
  ctx.stroke();
  ctx.restore();
}

function drawEdit(instance, ctx, sharedState) {
  drawRotatedRect(ctx, instance, roundColor, sharedState.lineWidth);
}

/**
 * 线段与 AABB 交点（局部坐标，轴对齐矩形）。
 * p1/p2 是线段两端点（局部坐标），hw/hh 是矩形半宽/半高。
 * @param {{x:number,y:number}} p1
 * @param {{x:number,y:number}} p2
 * @param {number} hw
 * @param {number} hh
 * @returns {{x:number,y:number}|null}
 */
function segmentAABBIntersection(p1, p2, hw, hh) {
  var dx = p2.x - p1.x;
  var dy = p2.y - p1.y;
  if (Math.abs(dx) < 1e-10 && Math.abs(dy) < 1e-10) return null;

  var tMin = 0;
  var tMax = 1;
  var bounds = [
    { d: dx, lo: -hw, hi: hw, p0: p1.x },
    { d: dy, lo: -hh, hi: hh, p0: p1.y }
  ];

  for (var i = 0; i < 2; i++) {
    var b = bounds[i];
    if (Math.abs(b.d) < 1e-10) {
      if (b.p0 < b.lo || b.p0 > b.hi) return null;
      continue;
    }
    var t1 = (b.lo - b.p0) / b.d;
    var t2 = (b.hi - b.p0) / b.d;
    if (t1 > t2) {
      var tmp = t1;
      t1 = t2;
      t2 = tmp;
    }
    if (t1 > tMin) tMin = t1;
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax) return null;
  }

  var t = tMin > 0 ? tMin : tMax;
  // 交点应在 p1 方向（从 p1 出发）
  return { x: p1.x + dx * t, y: p1.y + dy * t };
}

/**
 * 把 AABB 边界上的局部坐标点转为周长参数 t（0 ~ perimeter）。
 * 顺时针方向：上边(→) → 右边(↓) → 下边(←) → 左边(↑)。
 */
function pointToParam(p, hw, hh) {
  var w = hw * 2;
  var h = hh * 2;
  var eps = 0.01;
  if (Math.abs(p.y + hh) < eps) return p.x + hw;
  if (Math.abs(p.x - hw) < eps) return w + p.y + hh;
  if (Math.abs(p.y - hh) < eps) return w + h + (hw - p.x);
  return 2 * w + h + (hh - p.y);
}

/**
 * 把周长参数 t 转回 AABB 边界上的局部坐标点。
 */
function paramToPoint(t, hw, hh) {
  var w = hw * 2;
  var h = hh * 2;
  if (t <= w) return { x: t - hw, y: -hh };
  if (t <= w + h) return { x: hw, y: t - w - hh };
  if (t <= 2 * w + h) return { x: hw - (t - w - h), y: hh };
  return { x: -hw, y: hh - (t - 2 * w - h) };
}

/**
 * 局部坐标 → 世界坐标（旋转 + 平移）。
 */
function localToWorld(lp, instance) {
  var cosA = Math.cos(instance.rotationRad);
  var sinA = Math.sin(instance.rotationRad);
  return {
    x: instance.centerPoint.x + lp.x * cosA - lp.y * sinA,
    y: instance.centerPoint.y + lp.x * sinA + lp.y * cosA
  };
}

/**
 * 世界坐标 → 局部坐标。
 */
function worldToLocal(wp, instance) {
  var dx = wp[0] - instance.centerPoint.x;
  var dy = wp[1] - instance.centerPoint.y;
  var cosR = Math.cos(-instance.rotationRad);
  var sinR = Math.sin(-instance.rotationRad);
  return { x: dx * cosR - dy * sinR, y: dx * sinR + dy * cosR };
}

function drawPreview(instance, ctx, sharedState) {
  var points = sharedState.outlinePoints;
  var currentLineColor = sharedState.lineColor || lineColor;
  if (!points || !points.length) {
    drawRotatedRect(ctx, instance, currentLineColor, sharedState.lineWidth);
    return;
  }

  var hw = instance.width / 2;
  var hh = instance.height / 2;

  // 遍历刀线相邻点对，找出与矩形边界的交点（进入/退出）
  var intersections = [];
  for (var i = 0; i < points.length; i++) {
    var curLocal = worldToLocal(points[i], instance);
    var prevIdx = i === 0 ? points.length - 1 : i - 1;
    var prevLocal = worldToLocal(points[prevIdx], instance);

    var curInside = Math.abs(curLocal.x) <= hw && Math.abs(curLocal.y) <= hh;
    var prevInside = Math.abs(prevLocal.x) <= hw && Math.abs(prevLocal.y) <= hh;
    if (prevInside === curInside) continue;

    var inter = segmentAABBIntersection(prevLocal, curLocal, hw, hh);
    if (!inter) continue;

    intersections.push({
      local: inter,
      world: localToWorld(inter, instance),
      isEntry: !prevInside && curInside
    });
  }

  ctx.save();
  ctx.strokeStyle = currentLineColor;
  ctx.lineWidth = sharedState.lineWidth;
  ctx.setLineDash([]);

  // 画桥接线段：退出点 → 沿矩形周长 → 进入点（选较短路径）
  if (intersections.length >= 2) {
    var perimeter = instance.width * 2 + instance.height * 2;
    var step = Math.max(2, Math.min(perimeter / 40, 4));

    // 配对 (exit, entry)：intersections 按遍历顺序排列，exit 和 entry 交替出现
    for (var k = 1; k < intersections.length; k += 2) {
      var a = intersections[k - 1];
      var b = intersections[k];
      var exitPt = a.isEntry ? b : a;
      var entryPt = a.isEntry ? a : b;

      var t1 = pointToParam(exitPt.local, hw, hh);
      var t2 = pointToParam(entryPt.local, hw, hh);

      // 选较短路径（顺时针 vs 逆时针）
      var cwDist = t2 >= t1 ? t2 - t1 : perimeter - t1 + t2;
      var walkForward = cwDist <= perimeter / 2;

      ctx.beginPath();
      var firstWp = exitPt.world;
      ctx.moveTo(firstWp.x, firstWp.y);

      if (walkForward) {
        // 顺时针：t1 → t2
        if (t1 <= t2) {
          for (var t = t1 + step; t <= t2; t += step) {
            var lp = paramToPoint(t, hw, hh);
            var wp = localToWorld(lp, instance);
            ctx.lineTo(wp.x, wp.y);
          }
        } else {
          for (var ta = t1 + step; ta <= perimeter; ta += step) {
            var lpa = paramToPoint(ta, hw, hh);
            var wpa = localToWorld(lpa, instance);
            ctx.lineTo(wpa.x, wpa.y);
          }
          for (var tb = 0; tb <= t2; tb += step) {
            var lpb = paramToPoint(tb, hw, hh);
            var wpb = localToWorld(lpb, instance);
            ctx.lineTo(wpb.x, wpb.y);
          }
        }
      } else {
        // 逆时针：t1 → t2（递减 t，带环绕）
        if (t1 >= t2) {
          for (var tc = t1 - step; tc >= t2; tc -= step) {
            var lpc = paramToPoint(tc, hw, hh);
            var wpc = localToWorld(lpc, instance);
            ctx.lineTo(wpc.x, wpc.y);
          }
        } else {
          for (var td = t1 - step; td >= 0; td -= step) {
            var lpd = paramToPoint(td, hw, hh);
            var wpd = localToWorld(lpd, instance);
            ctx.lineTo(wpd.x, wpd.y);
          }
          for (var te = perimeter; te >= t2; te -= step) {
            var lpe = paramToPoint(te, hw, hh);
            var wpe = localToWorld(lpe, instance);
            ctx.lineTo(wpe.x, wpe.y);
          }
        }
      }
      ctx.lineTo(entryPt.world.x, entryPt.world.y);
      ctx.stroke();
    }
  }

  // 完整矩形红色描边
  drawRotatedRect(ctx, instance, currentLineColor, sharedState.lineWidth);
  ctx.restore();
}

function getCollisionShape(instance) {
  return {
    kind: 'orientedRect',
    cx: instance.centerPoint.x,
    cy: instance.centerPoint.y,
    w: instance.width,
    h: instance.height,
    rotationRad: instance.rotationRad
  };
}

function getSelectionRect(instance) {
  return {
    kind: 'oriented',
    cx: instance.centerPoint.x,
    cy: instance.centerPoint.y,
    w: instance.width,
    h: instance.height,
    rotationRad: instance.rotationRad
  };
}

function getMagnifierAnchor(instance) {
  return { x: instance.centerPoint.x, y: instance.centerPoint.y };
}

/**
 * icon 位置：矩形局部 +y 方向（下方），跟随 rotationRad 旋转。
 */
function getActionIconRect(instance) {
  const iconCfg = getBaseHoleIconConfig();
  const size = iconCfg.size;
  const cx = instance.centerPoint.x;
  const cy = instance.centerPoint.y;
  const distance = instance.height / 2 + iconCfg.marginTop + size / 2;
  const cos = Math.cos(instance.rotationRad);
  const sin = Math.sin(instance.rotationRad);
  const iconCx = cx - distance * sin;
  const iconCy = cy + distance * cos;
  return {
    x: iconCx - size / 2,
    y: iconCy - size / 2,
    w: size,
    h: size
  };
}

function drawActionIcon(instance, ctx /* , sharedState */) {
  if (!DEFAULT_CONFIG.baseHoleRotationEnabled) return;
  const img = loadBaseHoleRotateIcon();
  if (!img || !img.complete || !img.naturalWidth) return;
  const r = getActionIconRect(instance);
  ctx.drawImage(img, r.x, r.y, r.w, r.h);
}

function hitTestActionIcon(instance, point) {
  if (!DEFAULT_CONFIG.baseHoleRotationEnabled) return false;
  const r = getActionIconRect(instance);
  return (
    point.x >= r.x &&
    point.x <= r.x + r.w &&
    point.y >= r.y &&
    point.y <= r.y + r.h
  );
}

/**
 * icon 旋转手势：记录起手"点 - 矩形中心"的角度 baseAngle 与当前 rotationRad；
 * touchmove 时按相对增量更新 rotationRad，icon 始终跟在手指方向上。
 */
function onRotateIconStart(instance, point) {
  const angle = Math.atan2(
    point.y - instance.centerPoint.y,
    point.x - instance.centerPoint.x
  );
  instance._rotateRef = {
    baseAngle: angle,
    baseRotation: instance.rotationRad || 0,
    startPoint: { x: point.x, y: point.y },
    maxDelta: 0
  };
}

function onRotateIconMove(instance, point, sharedState) {
  const ref = instance._rotateRef;
  if (!ref) return;
  const dx = point.x - ref.startPoint.x;
  const dy = point.y - ref.startPoint.y;
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d > ref.maxDelta) ref.maxDelta = d;
  const angle = Math.atan2(
    point.y - instance.centerPoint.y,
    point.x - instance.centerPoint.x
  );
  instance.rotationRad = ref.baseRotation + (angle - ref.baseAngle);
  if (sharedState) {
    clampSelf(instance, sharedState);
  }
}

/**
 * icon 抬起：累计位移 < 阈值 = tap → 撤回拖动期间的旋转（tap 不响应）。
 */
function onRotateIconEnd(instance /* , sharedState */) {
  const ref = instance._rotateRef;
  const maxD = ref ? ref.maxDelta : 0;
  instance._rotateRef = null;
  if (ref && maxD < DEFAULT_CONFIG.iconTapMoveThresholdPx) {
    instance.rotationRad = ref.baseRotation;
  }
}

/**
 * 导出 layerData 片段。
 */
function toExport(instance, sharedState) {
  const backendPoint = convertCanvasToBackend(
    instance.centerPoint,
    sharedState
  );
  const ratioCenter = {
    x: +(backendPoint.x / sharedState.imageInfo.width).toFixed(6),
    y: +(backendPoint.y / sharedState.imageInfo.height).toFixed(6)
  };
  const k = sharedState.canvasScale || 1;
  const beWidth = Math.round(instance.width / k);
  const beHeight = Math.round(instance.height / k);
  const keep = 3;
  let w, h;
  if (sharedState.imageRatioOver) {
    w = Number((beWidth / sharedState.imageInfo.width).toFixed(keep));
    const base =
      sharedState.imageInfo.width /
      (sharedState.template.paperWidth / sharedState.template.paperHeight);
    h = Number((beHeight / base).toFixed(keep));
  } else {
    h = Number((beHeight / sharedState.imageInfo.height).toFixed(keep));
    const base =
      sharedState.imageInfo.height /
      (sharedState.template.paperWidth / sharedState.template.paperHeight);
    w = Number((beWidth / base).toFixed(keep));
  }
  const baseHoleItem = {
    centerPoint: ratioCenter,
    width: w,
    height: h,
    rotationRad: +instance.rotationRad.toFixed(6)
  };
  const originItem = {
    centerPoint: { x: instance.centerPoint.x, y: instance.centerPoint.y },
    width: instance.width,
    height: instance.height,
    rotationRad: instance.rotationRad,
    baseCanvasWidth: sharedState.canvasWidth,
    canvasRoundWhiteSpace: sharedState.canvasRoundWhiteSpace,
    componentId: instance.id,
    geometryConfig: instance.geometryConfig,
    sizePresetId: instance.sizePresetId || '',
    enableMagnifier: instance.enableMagnifier !== false
  };
  return {
    baseHoleInfoList: [baseHoleItem],
    originBaseHoleInfoList: [originItem]
  };
}

function serializeForDirty(instance) {
  return {
    cx: Math.round(instance.centerPoint.x * 100) / 100,
    cy: Math.round(instance.centerPoint.y * 100) / 100,
    rot: Math.round(instance.rotationRad * 1000) / 1000
  };
}

const baseHoleStrategy = {
  type: DRAW_TYPE.BASE_HOLE,
  validateGeometryConfig,
  init,
  clampSelf,
  hitTest,
  getHitTestInfo,
  onDragStart,
  onDragMove,
  onDragEnd,
  drawEdit,
  drawPreview,
  getCollisionShape,
  getSelectionRect,
  getMagnifierAnchor,
  getCornerRadius,
  drawActionIcon,
  hitTestActionIcon,
  onRotateIconStart,
  onRotateIconMove,
  onRotateIconEnd,
  toExport,
  serializeForDirty
};

registerComponentType(baseHoleStrategy);

export default baseHoleStrategy;
